// components/LanguageSwitcher.jsx
import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Check } from 'lucide-react';

const LANGS = [
  { code: 'kk', label: 'Қазақша' },
  { code: 'ru', label: 'Русский' },
  { code: 'en', label: 'English' },
];

// Короткая подпись на кнопке-триггере для каждого языка.
const SHORT_LABELS = { kk: 'KZ', ru: 'RU', en: 'EN' };

// Выпадающий переключатель языка интерфейса. <LanguageSwitcher variant="dark" /> для тёмного фона (лендинг-хиро),
// по умолчанию variant="light" для светлых панелей (навбар, сайдбар портала).
export const LanguageSwitcher = ({ className = '', variant = 'light' }) => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const current = i18n.resolvedLanguage || i18n.language || 'kk';

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const handleEscape = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const isDark = variant === 'dark';

  return (
    <div className={`relative ${className}`} ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen(prev => !prev)}
        className={`flex items-center gap-1 px-2.5 py-2 rounded-none transition-colors ${
          isDark
            ? 'text-white/70 hover:text-white hover:bg-white/10'
            : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
        }`}
        aria-label={t('LanguageSwitcher.interface_language')}
        aria-expanded={open}
      >
        <span className="text-[13px] font-black leading-none tracking-tight uppercase">
          {SHORT_LABELS[current] || 'RU'}
        </span>
        <ChevronDown size={13} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-52 max-w-[calc(100vw-2rem)] bg-white rounded-none shadow-2xl border border-gray-100 py-2 z-[60] text-left overflow-hidden">
          <p className="px-4 pt-1.5 pb-2 text-[10px] font-black uppercase tracking-widest text-gray-400">
            {t('LanguageSwitcher.interface_language')}
          </p>
          {LANGS.map(({ code, label }) => (
            <button
              key={code}
              type="button"
              onClick={() => { i18n.changeLanguage(code); setOpen(false); }}
              className="w-full flex items-center justify-between px-4 py-2.5 text-sm font-medium text-gray-800 hover:bg-gray-50 transition-colors"
            >
              <span>{label}</span>
              {current === code && <Check size={16} className="text-gray-900 shrink-0" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
